import { useCallback, useEffect, useState } from "react";
import type { AdminActivityEntry, AdminActivityLogEntry, AdminActivityLogResponse, Locale } from "../../shared/dto";
import { isTestTenant } from "../../shared/test-tenant";
import { TestFilterNote } from "./TestFilterNote";
import { clientPickerLabel, clientPickerOptions } from "./client-picker";
import { formatDate } from "./core";
import { openClientHandler } from "./OpenClient";
import "./admin-activity.css";
import { intlLanguage } from "../date-format";

/**
 * Servis wave 5: the activity feed becomes a filterable timeline.
 *
 * The old feed was a flat list of the last 40 events hidden in an accordion
 * between forms: no way to ask "what happened with the calls today", no way to
 * reach further back, and a client link that was the only context on offer.
 * The timeline keeps the same human sentences (no raw audit keys ever reach
 * the screen) and adds the three questions an operator actually asks: which
 * tool, what kind of outcome, and how far back.
 *
 * The tool and outcome verdicts come from the server (admin-diag.ts) so a
 * filter can never disagree with the row it returned.
 */

export const ACTIVITY_TOOL_IDS = [
  "calls", "team", "clients", "materials", "profile", "messaging", "imports", "access", "system"
] as const;

export const ACTIVITY_OUTCOME_IDS = ["error", "intervention", "progress"] as const;

export const ACTIVITY_PERIOD_DAYS = [1, 7, 30, 0] as const;

export interface ActivityFilters {
  tool: string;
  outcome: string;
  clientId: string;
  days: number;
}

export const DEFAULT_ACTIVITY_FILTERS: ActivityFilters = { tool: "all", outcome: "all", clientId: "", days: 7 };

/** One place builds the query, so the "load older" page carries the SAME
 * filters as the first one (a cursor with dropped filters silently changes
 * what the list means). */
export function activityLogQuery(
  filters: ActivityFilters,
  cursor: { at: string; id: string } | null,
  limit = 30
): string {
  const params = new URLSearchParams();
  if (filters.tool && filters.tool !== "all") params.set("tool", filters.tool);
  if (filters.outcome && filters.outcome !== "all") params.set("outcome", filters.outcome);
  if (filters.clientId) params.set("clientId", filters.clientId);
  params.set("days", String(filters.days));
  params.set("limit", String(limit));
  if (cursor) {
    params.set("beforeAt", cursor.at);
    params.set("beforeId", cursor.id);
  }
  return `/api/admin/activity-log?${params.toString()}`;
}

const ACTIVITY_PRESENTATION: Record<string, { titleKey: string; detailKey: string }> = {
  // 🔴 F138 kolečko 3. These twenty-two types were classified into a Servis
  // tool by the same round that gave them a Czech sentence in the client's
  // own history - and a type this map does not know answers "Provozní
  // změna" in the timeline, which is the generic sentence one surface over.
  // Classifying a type and naming it are ONE edit; the guard in
  // servis-w5 walks ACTIVITY_TOOL_TYPES and fails on any gap.
  app_release_status_changed: { titleKey: "staffConsole.activity.type.app_release_status_changed", detailKey: "staffConsole.activity.detail.app_release_status_changed" },
  bot_transfer_false_done_repaired: { titleKey: "staffConsole.activity.type.bot_transfer_false_done_repaired", detailKey: "staffConsole.activity.detail.bot_transfer_false_done_repaired" },
  bot_transfer_settings_updated: { titleKey: "staffConsole.activity.type.bot_transfer_settings_updated", detailKey: "staffConsole.activity.detail.bot_transfer_settings_updated" },
  bot_transferred_automatically: { titleKey: "staffConsole.activity.type.bot_transferred_automatically", detailKey: "staffConsole.activity.detail.bot_transferred_automatically" },
  global_unlock_link_changed: { titleKey: "staffConsole.activity.type.global_unlock_link_changed", detailKey: "staffConsole.activity.detail.global_unlock_link_changed" },
  instance_folder_recorded: { titleKey: "staffConsole.activity.type.instance_folder_recorded", detailKey: "staffConsole.activity.detail.instance_folder_recorded" },
  knowhow_handover_verified: { titleKey: "staffConsole.activity.type.knowhow_handover_verified", detailKey: "staffConsole.activity.detail.knowhow_handover_verified" },
  license_key_imported: { titleKey: "staffConsole.activity.type.license_key_imported", detailKey: "staffConsole.activity.detail.license_key_imported" },
  material_regeneration_requested: { titleKey: "staffConsole.activity.type.material_regeneration_requested", detailKey: "staffConsole.activity.detail.material_regeneration_requested" },
  notification_label_fallback: { titleKey: "staffConsole.activity.type.notification_label_fallback", detailKey: "staffConsole.activity.detail.notification_label_fallback" },
  order_links_confirmed: { titleKey: "staffConsole.activity.type.order_links_confirmed", detailKey: "staffConsole.activity.detail.order_links_confirmed" },
  order_links_integrated: { titleKey: "staffConsole.activity.type.order_links_integrated", detailKey: "staffConsole.activity.detail.order_links_integrated" },
  order_links_integration_requested: { titleKey: "staffConsole.activity.type.order_links_integration_requested", detailKey: "staffConsole.activity.detail.order_links_integration_requested" },
  owner_role_done: { titleKey: "staffConsole.activity.type.owner_role_done", detailKey: "staffConsole.activity.detail.owner_role_done" },
  owner_role_failed: { titleKey: "staffConsole.activity.type.owner_role_failed", detailKey: "staffConsole.activity.detail.owner_role_failed" },
  owner_role_id_refused: { titleKey: "staffConsole.activity.type.owner_role_id_refused", detailKey: "staffConsole.activity.detail.owner_role_id_refused" },
  owner_role_job_queued: { titleKey: "staffConsole.activity.type.owner_role_job_queued", detailKey: "staffConsole.activity.detail.owner_role_job_queued" },
  owner_role_settings_updated: { titleKey: "staffConsole.activity.type.owner_role_settings_updated", detailKey: "staffConsole.activity.detail.owner_role_settings_updated" },
  partner_brief_saved: { titleKey: "staffConsole.activity.type.partner_brief_saved", detailKey: "staffConsole.activity.detail.partner_brief_saved" },
  sales_edit_merged: { titleKey: "staffConsole.activity.type.sales_edit_merged", detailKey: "staffConsole.activity.detail.sales_edit_merged" },
  staff_override_approved: { titleKey: "staffConsole.activity.type.staff_override_approved", detailKey: "staffConsole.activity.detail.staff_override_approved" },
  sales_only_import: { titleKey: "staffConsole.activity.type.sales_only_import", detailKey: "staffConsole.activity.detail.sales_only_import" },
  training_resume_requested: { titleKey: "staffConsole.activity.type.training_resume_requested", detailKey: "staffConsole.activity.detail.training_resume_requested" },
  staff_created: { titleKey: "staffConsole.activity.type.staff_created", detailKey: "staffConsole.activity.detail.staff_created" },
  staff_role_changed: { titleKey: "staffConsole.activity.type.staff_role_changed", detailKey: "staffConsole.activity.detail.staff_role_changed" },
  staff_deactivated: { titleKey: "staffConsole.activity.type.staff_deactivated", detailKey: "staffConsole.activity.detail.staff_deactivated" },
  staff_reactivated: { titleKey: "staffConsole.activity.type.staff_reactivated", detailKey: "staffConsole.activity.detail.staff_reactivated" },
  call_created: { titleKey: "staffConsole.activity.type.call_created", detailKey: "staffConsole.activity.detail.call_created" },
  call_updated: { titleKey: "staffConsole.activity.type.call_updated", detailKey: "staffConsole.activity.detail.call_updated" },
  call_deleted: { titleKey: "staffConsole.activity.type.call_deleted", detailKey: "staffConsole.activity.detail.call_deleted" },
  unlock_code_created: { titleKey: "staffConsole.activity.type.unlock_code_created", detailKey: "staffConsole.activity.detail.unlock_code_created" },
  unlock_code_revoked: { titleKey: "staffConsole.activity.type.unlock_code_revoked", detailKey: "staffConsole.activity.detail.unlock_code_revoked" },
  resource_updated: { titleKey: "staffConsole.activity.type.resource_updated", detailKey: "staffConsole.activity.detail.resource_updated" },
  partner_message_sent: { titleKey: "staffConsole.activity.type.partner_message_sent", detailKey: "staffConsole.activity.detail.partner_message_sent" },
  impersonation_started: { titleKey: "staffConsole.activity.type.impersonation_started", detailKey: "staffConsole.activity.detail.impersonation_started" },
  impersonation_acting: { titleKey: "staffConsole.activity.type.impersonation_acting", detailKey: "staffConsole.activity.detail.impersonation_acting" },
  impersonation_looking: { titleKey: "staffConsole.activity.type.impersonation_looking", detailKey: "staffConsole.activity.detail.impersonation_looking" },
  impersonated_write: { titleKey: "staffConsole.activity.type.impersonated_write", detailKey: "staffConsole.activity.detail.impersonated_write" },
  sessions_revoked: { titleKey: "staffConsole.activity.type.sessions_revoked", detailKey: "staffConsole.activity.detail.sessions_revoked" },
  magic_link_issued: { titleKey: "staffConsole.activity.type.magic_link_issued", detailKey: "staffConsole.activity.detail.magic_link_issued" },
  profile_research_completed: { titleKey: "staffConsole.activity.type.profile_research_completed", detailKey: "staffConsole.activity.detail.profile_research_completed" },
  profile_research_failed: { titleKey: "staffConsole.activity.type.profile_research_failed", detailKey: "staffConsole.activity.detail.profile_research_failed" },
  deliverable_published: { titleKey: "staffConsole.activity.type.deliverable_published", detailKey: "staffConsole.activity.detail.deliverable_published" },
  notification_requeued: { titleKey: "staffConsole.activity.type.notification_requeued", detailKey: "staffConsole.activity.detail.notification_requeued" },
  transcript_requeued: { titleKey: "staffConsole.activity.type.transcript_requeued", detailKey: "staffConsole.activity.detail.transcript_requeued" },
  // S3-04: two acts of Servis itself. Unclassified, they landed in "Systém"
  // under the shrug "Pokud tuto změnu nepoznáváš, zkontroluj související
  // plochu." - about a change the reader had made thirty seconds earlier.
  staff_profile_updated: { titleKey: "staffConsole.activity.type.staff_profile_updated", detailKey: "staffConsole.activity.detail.staff_profile_updated" },
  notification_delivery_tested: { titleKey: "staffConsole.activity.type.notification_delivery_tested", detailKey: "staffConsole.activity.detail.notification_delivery_tested" }
};

const ACTIVITY_GROUPS: Array<{ types: string[]; key: string }> = [
  { types: ["profile_research_requested", "map_apply", "map_apply_completed", "map_apply_rejected", "profile_map_deleted"], key: "profile_change" },
  { types: ["deliverable_created", "deliverable_updated", "deliverable_client_approved", "deliverable_feedback_added", "feedback_processed", "feedback_dismissed", "feedback_note_deleted", "sales_edit_requested", "sales_edit_claimed", "sales_edit_reclaimed", "sales_edit_done", "sales_edit_failed", "dfy_build_launched", "price_approved", "sales_package_ready", "salespage_netlify_published", "salespage_netlify_domain_configured", "deliverable_deleted",
    // F169/F170 shipped these without a Servis sentence (servis-w5 guard was red).
    "prompt_rebuild_requested", "prompt_patch_requested", "prompt_patch_proposed", "prompt_patch_approved",
    "prompt_patch_rejected", "prompt_patch_applied", "prompt_patch_failed"], key: "material_change" },
  { types: ["call_cancelled", "call_enrolled", "call_enrollment_updated", "call_series_cancelled", "call_series_created", "call_series_extended", "unlock_code_renewed", "stable_call_link_redeemed", "stable_call_links_ensured", "marketing_call_completed"], key: "call_change" },
  { types: ["client_initialized", "client_updated", "client_confirmation_reverted", "manual_state_forward", "manual_state_regression", "milestone_set", "onboarding_completed", "beta_feedback_started", "knowhow_resources_ready", "knowhow_uploaded", "knowhow_folder_shared", "knowhow_opened_from_sharing_email", "buddy_ready", "stripe_invite_confirmed", "client_selling", "bot_transfer_ready", "bot_acceptance_reported", "app_upsell_interest", "apple_account_started", "client_step_confirmed", "questionnaire_submitted", "questionnaire_submission_reverted", "q1_ready", "q2_ready", "q2_submitted_waiting", "bot_transfer_job_queued", "bot_transfer_done", "bot_transfer_failed", "bot_transfer_returned_to_client", "bot_transfer_recipient_refused", "client_owner_email_changed", "milestone_deleted",
    // F171: the "Napojení plateb" step.
    "payments_setup_checklist_updated", "payments_setup_completed", "payments_setup_reopened", "payments_setup_flag_changed"], key: "client_progress" },
  // The sentence follows the SAME split as the tool chip: an import event that
  // read "Změněn provoz Launchpadu" under the chip "Importy" made the filter
  // and the row disagree about what the admin was looking at.
  { types: ["stripe_import_settings_updated", "answers_imported", "submission_exported"], key: "import_change" },
  { types: ["resource_deleted", "stripe_connect_url_set", "stripe_connected", "handover_telegram_submitted", "handover_owner_role_set", "partner_material_added", "partner_material_removed"], key: "client_resource" },
  // F138 kolečko 2: the T9 training lane writes both run events and
  // neither was listed, so a training that started or died read as
  // "Neznámá změna" in the feed opened to find out why a bot is quiet.
  { types: ["training_result_received", "training_run_started", "training_run_failed", "app_wizard_prefilled", "app_wizard_submitted", "ai_avatar_received", "instance_team_requested", "instance_team_done"], key: "automation_result" },
  { types: ["auth_login", "staff_client_viewed", "impersonation_ended", "impersonation_acting", "impersonation_looking", "impersonated_write", "invite_created", "invite_accepted", "invite_revoked"], key: "access_change" },
  // `dev_mail_allowlist_sent` belongs in "Systém" by right, but it still needs
  // a sentence: unlisted it read as "Neznámá změna" (S3-04).
  { types: ["r2_orphan_sweep", "dev_mail_allowlist_sent"], key: "system_change" }
];

for (const group of ACTIVITY_GROUPS) {
  for (const type of group.types) {
    ACTIVITY_PRESENTATION[type] = {
      titleKey: `staffConsole.activity.type.${group.key}`,
      detailKey: `staffConsole.activity.detail.${group.key}`
    };
  }
}

export function visibleAdminActivityEntries<T extends AdminActivityEntry>(entries: T[], hideTestTenants: boolean): T[] {
  if (!hideTestTenants) return entries;
  // The server computes isTest from name AND email (the one canonical
  // predicate); the name-only fallback covers cached payloads (audit a3-06).
  return entries.filter((entry) =>
    !(entry.isTest ?? isTestTenant(entry.clientName ?? "", ""))
    // A test administrator may work on a real client. Only clientless
    // probes inherit the actor's heuristic; linked work follows its client.
    && (Boolean(entry.clientId) || !isTestTenant(entry.actorName ?? "", "")));
}

export function presentAdminActivity(entry: AdminActivityEntry): { titleKey: string; detailKey: string } {
  return ACTIVITY_PRESENTATION[entry.type] ?? {
    titleKey: "staffConsole.activity.unknown",
    detailKey: "staffConsole.activity.unknownDetail"
  };
}

export function adminActivityStep(entry: AdminActivityEntry): string | null {
  const raw = entry.summary.kind ?? entry.summary.deliverableKind ?? entry.summary.gate;
  if (!raw) return null;
  if (["audit", "plan", "strategy"].includes(raw)) return "strategy";
  return ["salespage", "emails", "webinar", "avatar"].includes(raw) ? raw : null;
}

export function adminActivityPresentation(entry: AdminActivityEntry, t: (key: string) => string): { title: string; detail: string } {
  const base = presentAdminActivity(entry);
  const step = adminActivityStep(entry);
  if (!step) return { title: t(base.titleKey), detail: t(base.detailKey) };
  const material = t(`sales.kind.${step === "strategy" ? "plan" : step}`);
  const version = entry.summary.version ? `, ${t("staffConsole.activity.version").replace("{version}", entry.summary.version)}` : "";
  const action = entry.type.includes("failed") ? t("staffConsole.activity.materialFailed")
    : entry.type.includes("published") ? t("staffConsole.activity.materialPublished")
      : entry.type.includes("created") ? t("staffConsole.activity.materialCreated")
        : t("staffConsole.activity.materialUpdated");
  return {
    title: `${action}: ${material}${version}`,
    detail: t(base.detailKey)
  };
}

/**
 * Which material steps the console can actually open. The staff workspace has
 * one surface per RAIL step, and a step it does not know falls through to the
 * overview under the wrong heading (the P0 the rail already paid for once).
 * The avatar is a material without a rail step of its own (it lives in
 * attachments), so it names the row and never becomes a link target.
 */
const LINKABLE_STEPS = new Set(["strategy", "salespage", "emails", "webinar"]);

/** The row's way back into the work: the client detail, opened on the exact
 * step when the event names a material the console can show. */
export function activityContextLink(entry: AdminActivityEntry): string | null {
  if (!entry.clientId) return null;
  const step = adminActivityStep(entry);
  const target = step && LINKABLE_STEPS.has(step) ? step : null;
  return `/?client=${encodeURIComponent(entry.clientId)}${target ? `&staffStep=${encodeURIComponent(target)}` : ""}`;
}

function dayKey(value: string, locale: Locale): string {
  const date = new Date(value);
  return Number.isFinite(date.getTime())
    ? new Intl.DateTimeFormat(intlLanguage(locale), { dateStyle: "full" }).format(date)
    : value;
}

function timeOfDay(value: string, locale: Locale): string {
  const date = new Date(value);
  return Number.isFinite(date.getTime())
    ? new Intl.DateTimeFormat(intlLanguage(locale), { timeStyle: "short" }).format(date)
    : value;
}

/**
 * 🔴 The SECOND instance of the adjacent-row grouping the client history
 * carried (F138 kolečko 2, princip 3: a changed pattern is converted
 * everywhere, not where it was reported). This feed is keyset-paginated and
 * appends pages, so an out-of-order row is not hypothetical here either - and
 * a repeated day heading reads as a broken record rather than as unsorted
 * input. Sorting is the pane's own job, not the caller's.
 */
export function groupActivityByDay<T extends AdminActivityEntry>(entries: T[], locale: Locale): Array<{ day: string; entries: T[] }> {
  const stamp = (entry: T) => {
    const value = new Date(entry.createdAt).getTime();
    return Number.isFinite(value) ? value : Number.NEGATIVE_INFINITY;
  };
  const groups: Array<{ day: string; entries: T[] }> = [];
  for (const entry of [...entries].sort((left, right) => stamp(right) - stamp(left))) {
    const day = dayKey(entry.createdAt, locale);
    const current = groups.at(-1);
    if (current?.day === day) current.entries.push(entry);
    else groups.push({ day, entries: [entry] });
  }
  return groups;
}

export function AdminActivityRow(props: {
  entry: AdminActivityLogEntry;
  locale: Locale;
  t: (key: string) => string;
  onOpenClient?: (clientId: string) => void;
}) {
  const { entry, t } = props;
  const presentation = adminActivityPresentation(entry, t);
  const link = activityContextLink(entry);
  const actor = entry.actorName ?? t("staffConsole.activity.systemActor");
  return (
    <li className={`activity-row outcome-${entry.outcome}`} data-activity-outcome={entry.outcome}>
      <time className="activity-time" dateTime={entry.createdAt}>{timeOfDay(entry.createdAt, props.locale)}</time>
      <div className="activity-body">
        <span className="activity-type">{presentation.title}</span>
        <span className="activity-meta">{[actor, entry.clientName].filter(Boolean).join(" · ")}</span>
        <span className="activity-detail">{presentation.detail}</span>
        <span className="activity-chips">
          <span className="activity-chip">{t(`staffConsole.activityLog.tool.${entry.tool}`)}</span>
          <span className={`activity-chip outcome-${entry.outcome}`}>{t(`staffConsole.activityLog.outcome.${entry.outcome}`)}</span>
        </span>
      </div>
      {entry.clientId && link
        // S3-07: a feed row keeps its small link, but a plain click stays in
        // the app instead of reloading the console and every admin request.
        ? (
          <a className="activity-client-link" href={link}
            onClick={openClientHandler(entry.clientId, props.onOpenClient)}>
            {t("staffConsole.activity.openClient")}
          </a>
        )
        : <span className="activity-no-target">{t("staffConsole.activity.noTarget")}</span>}
    </li>
  );
}

export function AdminActivityFilters(props: {
  filters: ActivityFilters;
  counts: Record<string, number>;
  clients: Array<{ id: string; name: string; email: string }>;
  onChange: (filters: ActivityFilters) => void;
  t: (key: string) => string;
}) {
  const { filters, t } = props;
  const toolLabel = (id: string) => {
    const count = props.counts[id];
    const label = t(`staffConsole.activityLog.tool.${id}`);
    return typeof count === "number" ? `${label} (${count})` : label;
  };
  return (
    <div className="activity-filters">
      <label className="inline-select">
        <span>{t("staffConsole.activityLog.filterTool")}</span>
        <select value={filters.tool} onChange={(event) => props.onChange({ ...filters, tool: event.target.value })}>
          <option value="all">{t("staffConsole.activityLog.tool.all")}</option>
          {ACTIVITY_TOOL_IDS.map((id) => <option key={id} value={id}>{toolLabel(id)}</option>)}
        </select>
      </label>
      <label className="inline-select">
        <span>{t("staffConsole.activityLog.filterOutcome")}</span>
        <select value={filters.outcome} onChange={(event) => props.onChange({ ...filters, outcome: event.target.value })}>
          <option value="all">{t("staffConsole.activityLog.outcome.all")}</option>
          {ACTIVITY_OUTCOME_IDS.map((id) => (
            <option key={id} value={id}>{t(`staffConsole.activityLog.outcome.${id}`)}</option>
          ))}
        </select>
      </label>
      <label className="inline-select">
        <span>{t("staffConsole.activityLog.filterPeriod")}</span>
        <select value={String(filters.days)} onChange={(event) => props.onChange({ ...filters, days: Number(event.target.value) })}>
          {ACTIVITY_PERIOD_DAYS.map((days) => (
            <option key={days} value={String(days)}>{t(`staffConsole.activityLog.period.${days}`)}</option>
          ))}
        </select>
      </label>
      <label className="inline-select">
        <span>{t("staffConsole.activityLog.filterClient")}</span>
        <select value={filters.clientId} onChange={(event) => props.onChange({ ...filters, clientId: event.target.value })}>
          <option value="">{t("staffConsole.activityLog.allClients")}</option>
          {clientPickerOptions(props.clients).map((option) => (
            <option key={option.id} value={option.id}>{clientPickerLabel(option, t)}</option>
          ))}
        </select>
      </label>
    </div>
  );
}

export function AdminActivityLog(props: {
  t: (key: string) => string;
  locale: Locale;
  hideTestTenants: boolean;
  onHideTestTenantsChange?: (hide: boolean) => void;
  onOpenClient?: (clientId: string) => void;
  clients: Array<{ id: string; name: string; email: string }>;
}) {
  const { t } = props;
  const [filters, setFilters] = useState<ActivityFilters>(DEFAULT_ACTIVITY_FILTERS);
  const [entries, setEntries] = useState<AdminActivityLogEntry[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [cursor, setCursor] = useState<{ at: string; id: string } | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [state, setState] = useState<"loading" | "ready" | "error" | "appending">("loading");

  const load = useCallback(async (next: ActivityFilters, from: { at: string; id: string } | null) => {
    setState(from ? "appending" : "loading");
    try {
      const response = await fetch(activityLogQuery(next, from), { credentials: "include" });
      if (!response.ok) throw new Error("activity_log_failed");
      const body = await response.json() as AdminActivityLogResponse;
      setEntries((current) => from ? [...current, ...body.entries] : body.entries);
      setCounts(body.counts);
      setCursor(body.cursor);
      setHasMore(body.hasMore);
      setState("ready");
    } catch {
      setState("error");
    }
  }, []);

  useEffect(() => { void load(filters, null); }, [filters, load]);

  const visible = visibleAdminActivityEntries(entries, props.hideTestTenants);
  const hiddenTest = entries.length - visible.length;
  const groups = groupActivityByDay(visible, props.locale);

  return (
    // S2-12: the name and the sentence belong to the panel header (AdminPanel),
    // and the panel is called the same thing open as closed.
    <section className="activity-log" aria-label={t("staffConsole.activityLog.title")}>
      <AdminActivityFilters filters={filters} counts={counts} clients={props.clients} t={t}
        onChange={(next) => { setCursor(null); setFilters(next); }} />
      <TestFilterNote shown={visible.length} hidden={hiddenTest} t={t}
        onShowTest={props.onHideTestTenantsChange && (() => props.onHideTestTenantsChange?.(false))} />
      {state === "loading" && <p className="staff-empty">{t("common.loading")}</p>}
      {state === "error" && (
        <div className="activity-error">
          <p className="message error-message" role="alert">{t("staffConsole.activityLog.loadError")}</p>
          <button className="secondary-button compact-button" type="button" onClick={() => void load(filters, null)}>
            {t("common.retry")}
          </button>
        </div>
      )}
      {state !== "loading" && state !== "error" && visible.length === 0 && (
        <p className="staff-empty">{t("staffConsole.activityLog.empty")}</p>
      )}
      {groups.map((group) => (
        <div className="activity-day" key={group.day}>
          <h5 className="activity-day-title">{group.day}</h5>
          <ul className="activity-feed">
            {group.entries.map((entry) => (
              <AdminActivityRow key={entry.id} entry={entry} locale={props.locale} t={t}
                onOpenClient={props.onOpenClient} />
            ))}
          </ul>
        </div>
      ))}
      {hasMore && (
        <button className="secondary-button" type="button" disabled={state === "appending"}
          onClick={() => void load(filters, cursor)}>
          {state === "appending" ? t("staffConsole.activityLog.loadingMore") : t("staffConsole.activityLog.loadMore")}
        </button>
      )}
      {!hasMore && visible.length > 0 && (
        <p className="activity-end">{t("staffConsole.activityLog.end").replace("{date}", formatDate(entries.at(-1)?.createdAt ?? "", props.locale))}</p>
      )}
    </section>
  );
}
